import { useState } from 'react'
import { FileText, Search, Sparkles } from 'lucide-react'
import { SHORTCUTS_SECTIONS, ShortcutSection, ShortcutCategory, ShortcutSubcategory } from '../../config/shortcuts'
import ShortcutSectionModal from './ShortcutSectionModal'

interface ShortcutGridProps {
  onShortcutSelect: (categoryId: string, subcategoryId: string) => void
}

// Icons for main sections
const SECTION_ICONS: Record<string, typeof Sparkles> = {
  document_generation: Sparkles,
  document_analysis: Search,
  legal_research: FileText,
}

// Colors for main sections
const SECTION_COLORS: Record<string, { bg: string; bgHover: string; border: string }> = {
  document_generation: { bg: 'bg-blue-500', bgHover: 'hover:bg-blue-600', border: 'border-blue-200' },
  document_analysis: { bg: 'bg-purple-500', bgHover: 'hover:bg-purple-600', border: 'border-purple-200' },
  legal_research: { bg: 'bg-emerald-500', bgHover: 'hover:bg-emerald-600', border: 'border-emerald-200' },
}

export default function ShortcutGrid({ onShortcutSelect }: ShortcutGridProps) {
  const [selectedSection, setSelectedSection] = useState<ShortcutSection | null>(null)

  const handleSectionClick = (section: ShortcutSection) => {
    setSelectedSection(section)
  }

  const handleSubcategorySelect = (category: ShortcutCategory, subcategory: ShortcutSubcategory) => {
    onShortcutSelect(category.id, subcategory.id)
    setSelectedSection(null)
  }

  const handleModalClose = () => {
    setSelectedSection(null)
  }

  return (
    <>
      {/* Section header */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Szybki start</h2>
        <p className="text-sm text-gray-500">Wybierz obszar pracy i zacznij z gotowym schematem działania</p>
      </div>

      {/* 3 main section buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {SHORTCUTS_SECTIONS.map((section) => {
          const Icon = SECTION_ICONS[section.id] || Sparkles
          const colors = SECTION_COLORS[section.id] || SECTION_COLORS.document_generation

          return (
            <button
              key={section.id}
              onClick={() => handleSectionClick(section)}
              className={`flex items-center p-4 bg-white border ${colors.border} rounded-lg hover:shadow-md transition-all group`}
            >
              <div className={`p-3 ${colors.bg} ${colors.bgHover} rounded-lg transition-colors`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div className="ml-4 text-left">
                <h3 className="font-semibold text-gray-900 group-hover:text-gray-700">
                  {section.label}
                </h3>
                <p className="text-sm text-gray-500">
                  {section.categories.length} kategorii
                </p>
              </div>
            </button>
          )
        })}
      </div>

      {/* Modal for category/subcategory selection */}
      {selectedSection && (
        <ShortcutSectionModal
          section={selectedSection}
          onSelect={handleSubcategorySelect}
          onClose={handleModalClose}
        />
      )}
    </>
  )
}
